<template>
  <div class="container-fluid pb-4">
    <div v-if="loading" class="text-muted">Загрузка...</div>
    <div v-else-if="region" class="info-sections">
      <div class="card info-card">
        <h3 class="card-header"><span class="text-caps">{{ sectionTitle('Основное') }}</span></h3>
        <div class="card-body">
          <div v-if="mainFields.length" class="info-fields">
            <div
              v-for="field in mainFields"
              :key="field.label"
              class="info-field"
              :class="{ 'info-field--inactive': field.inactive }"
            >
              <span class="info-field__label">{{ field.label }}:</span>
              <span class="info-field__value">{{ formatValue(field.value) }}</span>
            </div>
          </div>
          <div v-else class="text-muted">Нет данных</div>
        </div>
      </div>

      <div class="card info-card">
        <h3 class="card-header"><span class="text-caps">{{ sectionTitle('Регион') }}</span></h3>
        <div class="card-body">
          <div v-if="regionOrganizationFields.length" class="info-fields">
            <div
              v-for="field in regionOrganizationFields"
              :key="field.key"
              class="info-field"
            >
              <span class="info-field__label">{{ field.label }}:</span>
              <template v-if="!field.children">
                <span class="info-field__value">{{ formatValue(field.value) }}</span>
              </template>
              <template v-else>
                <div class="info-field__children">
                  <div
                    v-for="child in field.children"
                    :key="child.key"
                    class="info-field"
                  >
                    <span class="info-field__label">{{ child.label }}:</span>
                    <span class="info-field__value">{{ formatValue(child.value) }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <div v-else class="text-muted">Нет данных</div>
        </div>
      </div>

      <div class="card info-card">
        <h3 class="card-header"><span class="text-caps">{{ sectionTitle('Дивизионы') }}</span></h3>
        <div class="card-body">
          <div v-if="divisionDetails.length" class="info-fields">
            <div
              v-for="detail in divisionDetails"
              :key="detail.division.id || detail.index"
              class="division-block"
            >
              <div v-if="detail.index" class="division-separator" />
              <div class="division-heading">{{ divisionTitle(detail.division, detail.index) }}</div>
              <div v-if="detail.fields.length">
                <div
                  v-for="field in detail.fields"
                  :key="field.key"
                  class="info-field"
                >
                  <span class="info-field__label">{{ field.label }}:</span>
                  <template v-if="!field.children">
                    <span class="info-field__value">{{ formatValue(field.value) }}</span>
                  </template>
                  <template v-else>
                    <div class="info-field__children">
                      <div
                        v-for="child in field.children"
                        :key="child.key"
                        class="info-field"
                      >
                        <span class="info-field__label">{{ child.label }}:</span>
                        <span class="info-field__value">{{ formatValue(child.value) }}</span>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
              <div v-else class="text-muted">Нет данных</div>
            </div>
          </div>
          <div v-else class="text-muted">Дивизионы отсутствуют</div>
        </div>
      </div>
    </div>
    <div v-else-if="loadError" class="text-muted">Организация не найдена</div>
    <div v-else class="text-muted">Нет данных</div>
  </div>
</template>

<script>
  import moment from 'moment'

  export default {
    props: {
      region: {
        type: Object,
        default: null,
      },
      creator: {
        type: Object,
        default: null,
      },
      divisions: {
        type: Array,
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
      loadError: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      mainFields () {
        if (!this.region) {
          return []
        }

        const discount = this.discountInfo

        return [
          { label: 'Баланс', value: this.formatMoney(this.region.money) },
          { label: 'Тариф (выбранный)', value: this.getTariffName(this.region.subscription) },
          { label: 'Тариф (действующий)', value: this.getTariffName(this.region.current_subscription) },
          { label: 'Администратор', value: this.creator ? this.creator.name : null },
          { label: 'Email администратора', value: this.creator ? this.creator.email : null },
          { label: 'Телефон администратора', value: this.creator ? this.creator.phone : null },
          { label: 'Часовой пояс', value: this.region.gmt },
          { label: 'Скидка', value: discount.text, inactive: !discount.active && discount.text !== '—' },
        ]
      },
      regionOrganizationFields () {
        return this.buildOrganizationFields(this.region && this.region.organization)
      },
      divisionDetails () {
        return (this.divisions || []).map((division, index) => ({
          division,
          fields: this.buildOrganizationFields(division && division.organization),
          index,
        }))
      },
      discountInfo () {
        if (!this.region || this.region.sale === null || this.region.sale === undefined) {
          return { text: '—', active: true }
        }

        const saleValue = Number(this.region.sale)
        let saleText
        if (!Number.isNaN(saleValue)) {
          const formattedSale = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(saleValue)
          saleText = `${formattedSale} %`
        } else {
          saleText = `${this.region.sale}`
        }

        const dateText = this.region.sale_date
          ? moment.parseZone(this.region.sale_date).format('DD.MM.YYYY HH:mm')
          : null

        if (dateText) {
          saleText += ` до ${dateText}`
        }

        return {
          text: saleText,
          active: !!this.region.sale_active,
        }
      },
    },
    methods: {
      formatValue (value) {
        if (value === null || value === undefined || value === '') {
          return '—'
        }

        return value
      },
      formatMoney (value) {
        if (value === null || value === undefined || value === '') {
          return '—'
        }

        const numberValue = Number(value)
        if (Number.isNaN(numberValue)) {
          return value
        }

        return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(numberValue)
      },
      getTariffName (tariff) {
        if (!tariff) {
          return '—'
        }

        if (typeof tariff === 'string') {
          return tariff
        }

        return tariff.name || tariff.code || '—'
      },
      sectionTitle (title) {
        const shortName = (this.region && (this.region.short_name || this.region.name)) || 'Организация'
        return `${shortName} — ${title}`
      },
      buildOrganizationFields (organization) {
        if (!organization) {
          return []
        }

        const fields = [
          { key: 'bik', label: 'БИК', value: organization.bik },
          { key: 'gmt', label: 'Часовой пояс', value: organization.gmt },
          { key: 'inn', label: 'ИНН', value: organization.inn },
          { key: 'tax', label: 'Система налогообложения', value: organization.tax },
          { key: 'bank', label: 'Банк', value: organization.bank },
          { key: 'name', label: 'Полное название', value: organization.name },
          { key: 'ogrn', label: 'ОГРН', value: organization.ogrn },
          { key: 'rate', label: 'Ставка', value: organization.rate },
          { key: 'brief', label: 'Короткое название', value: organization.brief },
          { key: 'email', label: 'Email', value: organization.email },
        ]

        if (organization.signer && typeof organization.signer === 'object') {
          const signer = organization.signer
          fields.push({
            key: 'signer',
            label: 'Подписант',
            children: [
              { key: 'signer_base', label: 'Основание', value: signer.base },
              { key: 'signer_number', label: 'Номер', value: signer.number },
              { key: 'signer_from', label: 'Дата', value: this.formatDate(signer.from) },
              { key: 'signer_role', label: 'Роль', value: signer.role },
              { key: 'signer_second_name', label: 'Фамилия', value: signer.second_name },
              { key: 'signer_first_name', label: 'Имя', value: signer.first_name },
              { key: 'signer_third_name', label: 'Отчество', value: signer.third_name },
            ],
          })
        }

        fields.push(
          { key: 'legal_address', label: 'Юридический адрес', value: organization.legal_address },
          { key: 'actual_address', label: 'Фактический адрес', value: organization.actual_address },
          { key: 'checking_account', label: 'Расчётный счёт', value: organization.checking_account },
          { key: 'correspondent_account', label: 'Корреспондентский счёт', value: organization.correspondent_account },
        )

        return fields
      },
      divisionTitle (division, index) {
        const position = index + 1
        if (!division) {
          return `Дивизион #${position}`
        }

        return division.name || `Дивизион #${position}`
      },
      formatDate (value, format = 'DD.MM.YYYY') {
        if (!value) {
          return value
        }

        const date = moment(value)
        if (!date.isValid()) {
          return value
        }

        return date.format(format)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .info-sections {
    display: flex;
    flex-direction: column;
  }

  .info-card + .info-card {
    margin-top: 1.5rem;
  }

  .info-fields {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .info-field {
    display: flex;
    flex-wrap: wrap;
    font-size: 0.95rem;
  }

  .info-field__label {
    font-weight: 600;
    margin-right: 0.5rem;
    white-space: nowrap;
  }

  .info-field__value {
    flex: 1;
    min-width: 200px;
  }

  .info-field__children {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-left: 1.5rem;
    margin-top: 0.5rem;
  }

  .info-field--inactive .info-field__value {
    text-decoration: line-through;
    opacity: 0.4;
  }

  .division-block + .division-block {
    margin-top: 1.5rem;
  }

  .division-separator {
    height: 1px;
    background: rgba(0, 0, 0, 0.08);
    margin-bottom: 1rem;
  }

  .division-heading {
    font-weight: 600;
    margin-bottom: 0.75rem;
  }
</style>



// WEBPACK FOOTER //
// resources/assets/js/pages/billing/organization/info.vue